import { useEffect, useState, type FormEvent } from "react";
import { apiRequest } from "../api";
import { Button, Field, SessionAction, Shell } from "../components";
import {
  buildDeviceLoginAction,
  completeBomanaLauncherAuthorization,
  describeDeviceAuthFailure,
  normalizeDeviceCode,
  readBomanaLauncherMode,
} from "../device-auth-flow";
import { setPageMetadata } from "../page-metadata";

type DeviceAction = "inspect" | "approve" | "deny" | null;
type MessageTone = "success" | "error" | "info";

export function DevicePage() {
  const initialCode = new URLSearchParams(window.location.search).get(
    "user_code",
  );
  const [code, setCode] = useState(initialCode ?? "");
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState<MessageTone>("info");
  const [verified, setVerified] = useState(false);
  const [loginRequired, setLoginRequired] = useState(false);
  const [action, setAction] = useState<DeviceAction>(null);
  const launcherMode = readBomanaLauncherMode(window.location.search);
  const launcherFlow = launcherMode !== null;
  const loginAction = buildDeviceLoginAction(code, launcherMode);
  const busy = action !== null;

  useEffect(() => {
    setPageMetadata(
      launcherFlow ? "授权 Bomana Launcher | CheemsPay" : "授权桌面应用 | CheemsPay",
      launcherMode === "popup"
        ? "在独立窗口中确认 Bomana Launcher 登录请求。"
        : launcherMode === "redirect"
          ? "确认后自动返回 Bomana Launcher。"
        : "确认并批准你正在使用的 CheemsPay 桌面应用登录请求。",
    );
  }, [launcherFlow, launcherMode]);

  const inspect = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) {
      return;
    }
    const normalizedCode = normalizeDeviceCode(code);
    setCode(normalizedCode);
    setAction("inspect");
    setMessage("");
    try {
      await apiRequest(
        `/api/auth/device?user_code=${encodeURIComponent(normalizedCode)}`,
      );
      try {
        await apiRequest("/api/session");
        setLoginRequired(false);
        setVerified(true);
        setMessageTone("info");
        setMessage("设备请求已确认。请确认这是你正在操作的设备，然后选择批准或拒绝。");
      } catch (error) {
        const failure = describeDeviceAuthFailure(error);
        setLoginRequired(failure.loginRequired);
        setVerified(failure.loginRequired);
        setMessageTone("error");
        setMessage(failure.message);
      }
    } catch (error) {
      setVerified(false);
      setLoginRequired(false);
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "设备码无效");
    } finally {
      setAction(null);
    }
  };

  const decide = async (accept: boolean) => {
    if (busy) {
      return;
    }
    setAction(accept ? "approve" : "deny");
    setMessage("");
    try {
      await apiRequest(
        accept ? "/api/auth/device/approve" : "/api/auth/device/deny",
        {
          method: "POST",
          body: JSON.stringify({ userCode: code }),
        },
      );
      setMessageTone("success");
      setMessage(
        accept
          ? launcherFlow
            ? "设备已授权，正在返回 Bomana Launcher。"
            : "设备已授权。请返回正在登录的桌面应用完成这次登录；完成后可关闭此页，或回到账户中心查看状态。"
          : "已拒绝此设备。你可以关闭此页，或回到账户中心查看状态；如需处理另一台设备，请输入新的设备码。",
      );
      setVerified(false);
      setLoginRequired(false);
      if (accept && launcherFlow) {
        completeBomanaLauncherAuthorization(window.location.search, code, {
          opener: window.opener,
          close: () => window.close(),
          replace: (url) => window.location.replace(url),
        });
      }
    } catch (error) {
      const failure = describeDeviceAuthFailure(error);
      setLoginRequired(failure.loginRequired);
      setVerified(true);
      setMessageTone("error");
      setMessage(failure.message);
    } finally {
      setAction(null);
    }
  };

  return (
    <Shell
      action={<SessionAction signedOutReturnTo={window.location.pathname + window.location.search} />}
      back={{ href: "/account", label: "返回账户" }}
    >
      <section className="single-card-page">
        <div aria-busy={busy} className="single-card">
          <h1>{launcherFlow ? "授权 Bomana Launcher" : "授权桌面应用"}</h1>
          <p>{launcherFlow ? "完成后会自动回到 Bomana Launcher。" : "只批准你正在使用的设备。不要把密码输入桌面客户端。"}</p>
          <form onSubmit={inspect}>
            <Field label="8 位设备码">
              <input
                autoFocus
                disabled={busy}
                maxLength={8}
                onChange={(event) => {
                  setCode(event.target.value.toUpperCase());
                  setVerified(false);
                  setLoginRequired(false);
                  setMessage("");
                }}
                placeholder="ABCD2345"
                required
                value={code}
              />
            </Field>
            {!verified ? (
              <Button disabled={busy} type="submit">
                {action === "inspect" ? "正在检查…" : "检查设备请求"}
              </Button>
            ) : null}
          </form>
          {verified && !loginRequired ? (
            <div className="decision-row">
              <Button
                disabled={busy}
                onClick={() => void decide(true)}
              >
                {action === "approve" ? "正在批准…" : "批准设备"}
              </Button>
              <Button
                className="secondary"
                disabled={busy}
                onClick={() => void decide(false)}
              >
                {action === "deny" ? "正在拒绝…" : "拒绝"}
              </Button>
            </div>
          ) : null}
          {verified && loginRequired ? (
            <div className="decision-row login-required">
              <a className="button" href={loginAction.href}>
                {loginAction.label}后继续此设备授权
              </a>
            </div>
          ) : null}
          {message ? (
            <>
              <div
                className={
                  messageTone === "error" ? "form-error" : "form-message"
                }
                role={messageTone === "error" ? "alert" : "status"}
              >
                {message}
              </div>
              {messageTone === "success" ? (
                <div className="decision-row login-required">
                  <a className="button secondary" href="/account">
                    返回账户中心
                  </a>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </section>
    </Shell>
  );
}
